import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Table, Tooltip } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import { clone, get, last } from 'lodash';
import { Mutation, useMutation, useQuery } from 'react-apollo';
import QcardPanel from '_Components/QcardPanel/QcardPanel';
import QTitle from '_Components/Texts/QTitle/QTitle';
import QButton from '_Components/Buttons/QButton/QButton';
import QCheckbox from '_Components/Fields/QCheckbox/QCheckbox';
import Icon from '_Components/Icons/Icon';
import { black, grayShades, green, lightBlue, red, white } from 'styles/abstracts/colors';
import { fontSizes } from 'styles/abstracts/typography';
import { connect } from 'react-redux';
import QSelect from '_Components/Fields/QSelect/QSelect';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import { setThematicSelectionList } from '_Resources/Cards/Actions/setThematicSelectionList';
import Loader from '_Components/Loader/OnLoad';
import TableRow from './_Components/TableRow/TableRow';
import SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION from '../graphql/saveThematicsOverviewSettings';
import GET_THEMATICS_OVERVIEW_DATA_QUERY from '../graphql/getThematicsOverviewData';
import GET_THEMATIC_SELECTION_LIST from '../graphql/getThematicSelectionList';
import SAVE_THEMATIC_SELECTION_MUTATION from '../graphql/saveThematicSelection';
import DELETE_THEMATIC_SELECTION_MUTATION from '../graphql/deleteThematicsSelection';
import ListTitle from './_Components/SelectionList/ListTitle';
import EditableTitle from './_Components/EditableTitle/EditableTitle';
import DeleteSelectionConfirmationModal from './_Components/DeleteSelectionConfirmationModal/DeleteSelectionConfirmationModal';
import { getSelectedThematicsForSettings } from '../CategoryBreakdownCard/utils';

const StyledQPanel = styled(QcardPanel)`
  padding: 1em 0;
`;

const Title = styled(QTitle)`
  padding: 0 1em;
  margin-bottom: 1em;
`;

const UpRightSpan = styled.span`
  position: relative;
  top: -0.5em;
  font-size: 0.75em;
`;

const TableContainer = styled.div`
  overflow-y: scroll;
  max-height: 62.5vh;
`;

const Head = styled.thead`
  border-bottom: 1px solid ${grayShades.g300};
  border-top: 1px solid ${grayShades.g300};
`;

const StyledCell = styled.th`
  color: ${grayShades.g600};
  font-size: ${fontSizes.default};
  padding: 1em !important;
`;

const TitleCell = styled(StyledCell)`
  width: 33em !important;
`;

const IndicatorCell = styled(StyledCell)`
  width: 9em !important;
  text-align: center;
`;

const CheckboxCell = styled(StyledCell)`
  text-align: center;
  width: 2.5rem;
`;

const Checkbox = styled(QCheckbox)`
  margin: 0;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ButtonsContainer = styled.div`
  margin-right: 3em;
  display: flex;
  flex-direction: row;
  justify-content: ${({ alignRight }) => (alignRight ? 'right' : 'space-between')};
  width: 26%;
  align-items: start;
`;

const StyledQButton = styled(QButton)`
  display: flex;
  align-items: center;

  & > * {
    margin: 0 0.25em;
  }
`;

const ThematicSelectionSelect = styled(QSelect)``;

const DisplayAllButton = styled.span`
  margin-bottom: 1em;
  margin-left: 1em;
  font-weight: bold;
  cursor: pointer;
`;

const EmailMessageContainer = styled.div`
  color: ${lightBlue};
  font-weight: bold;
  text-align: center;
`;

export const sortByName = cat => {
  cat.sort((a, b) => (a.thematicsOverviewDetails.name > b.thematicsOverviewDetails.name ? 1 : -1));
  return cat;
};

const CategoryHierarchyCard = ({
  thematics,
  t,
  settings,
  projectId,
  range,
  filterKeys,
  allSelectedCategories,
  hints,
  message,
  selectionList,
  selectedThematicSelectionOption,
  dispatchSelectThematicSelection,
  dispatchSetThematicSelectionList,
  currentTab,
  aiUsageExpirationDate,
  currentMentionCountWithoutNull,
}) => {
  const [categories, setCategories] = useState(thematics);
  const [showSelection, setShowSelection] = useState(false);
  const [selectedThematics, setSelectedThematics] = useState([]);
  const [isGlobalSelected, setGlobalSelected] = useState(true);
  const [displayAll, setDisplayAll] = useState(false);
  const [creationMode, setCreationMode] = useState(false);
  const [managementMode, setManagementMode] = useState(false);
  const [selectionTitle, setSelectionTitle] = useState(selectedThematicSelectionOption?.name || '');
  const [selectionDescription, setSelectionDescription] = useState(selectedThematicSelectionOption?.description || '');
  const [deleteSelectionConfirmationModalOpen, toggleDeleteSelectionConfirmationModal] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [totalThematicsMentionCount, setTotalThematicsMentionCount] = useState(1);
  const [saveThematicsOverviewSettings, { loading: loadingThematicOverview }] = useMutation(
    SAVE_THEMATICS_OVERVIEW_SETTINGS_MUTATION,
  );
  const [saveThematicSelection] = useMutation(SAVE_THEMATIC_SELECTION_MUTATION);
  const [duplicateThematicSelection] = useMutation(SAVE_THEMATIC_SELECTION_MUTATION, {
    refetchQueries: [{ query: GET_THEMATIC_SELECTION_LIST, variables: { projectId } }],
    onCompleted: data => {
      if (data.saveThematicSelection.errors === null || data.saveThematicSelection.errors.length === 0) {
        setManagementMode(false);
        setShowSelection(false);
        const duplicatedThematicSelectionToStore = data.saveThematicSelection.selection;
        duplicatedThematicSelectionToStore.label = data.saveThematicSelection.selection.name;
        duplicatedThematicSelectionToStore.projectId = projectId;
        dispatchSelectThematicSelection(duplicatedThematicSelectionToStore);
      }
    },
  });
  const { data: dataThematicSelectionList } = useQuery(GET_THEMATIC_SELECTION_LIST, {
    fetchPolicy: 'cache-and-network',
    variables: { projectId },
  });

  const onThematicSelectionChange = selectThematicSelectionOption => {
    dispatchSelectThematicSelection(selectThematicSelectionOption);
    const cloneSettings = clone(settings);
    cloneSettings.selectedThematics = getSelectedThematicsForSettings(selectThematicSelectionOption.thematics);
    saveThematicsOverviewSettings({
      variables: {
        projectId,
        settings: JSON.stringify(cloneSettings),
        thematicSelectionId: selectThematicSelectionOption?.id || null,
      },
      refetchQueries: () => {
        if (!projectId || !range) return [];
        return [
          {
            query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
            fetchPolicy: 'cache-and-network',
            variables: {
              ...filterKeys,
              projectId,
              range,
              thematicSelectionId: selectThematicSelectionOption?.id || null,
            },
          },
        ];
      },
      awaitRefetchQueries: true,
    });
    setSelectedThematics(getSelectedThematicsForSettings(selectThematicSelectionOption.thematics));
    setTotalThematicsMentionCount(currentMentionCountWithoutNull);
  };
  useEffect(() => {
    setCategories(sortByName(thematics));
    setTotalThematicsMentionCount(currentMentionCountWithoutNull);
  }, [thematics]);

  useEffect(() => {
    if (!creationMode) {
      setSelectedThematics(settings?.selectedThematics);
    }
    setTotalThematicsMentionCount(currentMentionCountWithoutNull);
  }, [settings, currentTab, selectedThematicSelectionOption?.id]);

  const findAllByKey = (obj, keyToFind) => {
    return (
      obj &&
      Object.entries(obj).reduce(
        (acc, [key, value]) =>
          key === keyToFind
            ? acc.concat(value)
            : typeof value === 'object'
            ? acc.concat(findAllByKey(value, keyToFind))
            : acc,
        [],
      )
    );
  };

  useEffect(() => {
    if (selectedThematics && allSelectedCategories && selectedThematics.length === allSelectedCategories.length) {
      setGlobalSelected(true);
    } else {
      setGlobalSelected(false);
    }
    setTotalThematicsMentionCount(currentMentionCountWithoutNull);
  }, [selectedThematics]);

  useEffect(() => {
    if (dataThematicSelectionList) {
      const optionsList = dataThematicSelectionList.getThematicSelectionList.map(selection => ({
        ...selection,
        label: selection.name,
      }));

      dispatchSetThematicSelectionList(optionsList);
      const foundStoredThematicSelectionId = optionsList
        .map(option => option.id)
        .find(id => id === selectedThematicSelectionOption?.id);
      if (foundStoredThematicSelectionId) {
        dispatchSelectThematicSelection(
          optionsList.find(thematicSelection => thematicSelection.id === selectedThematicSelectionOption?.id),
        );
      }
      if (!foundStoredThematicSelectionId) {
        dispatchSelectThematicSelection(last(optionsList));
        const cloneSettings = clone(settings);
        const thematicsVariableForSettings = getSelectedThematicsForSettings(last(optionsList)?.thematics);
        cloneSettings.selectedThematics = thematicsVariableForSettings || [];
        saveThematicsOverviewSettings({
          variables: {
            projectId,
            settings: JSON.stringify(cloneSettings),
            thematicSelectionId: last(optionsList)?.id || null,
          },
          refetchQueries: () => {
            if (!projectId || !range) return [];
            return [
              {
                query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
                variables: { ...filterKeys, projectId, range, thematicSelectionId: last(optionsList)?.id || null },
              },
            ];
          },
          awaitRefetchQueries: true,
        });
      }
      setTotalThematicsMentionCount(currentMentionCountWithoutNull);
    }
  }, [dataThematicSelectionList]);

  useEffect(() => {
    if (selectedThematicSelectionOption === null && selectionList.length > 0) {
      dispatchSelectThematicSelection(last(selectionList));
      const cloneSettings = clone(settings);
      cloneSettings.selectedThematics = getSelectedThematicsForSettings(last(selectionList).thematics);
      saveThematicsOverviewSettings({
        variables: {
          projectId,
          settings: JSON.stringify(cloneSettings),
          thematicSelectionId: last(selectionList).id,
        },
        refetchQueries: () => {
          if (!projectId || !range) return [];
          return [
            {
              query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
              variables: { ...filterKeys, projectId, range, thematicSelectionId: last(selectionList)?.id || null },
            },
          ];
        },
        awaitRefetchQueries: true,
      });
      setTotalThematicsMentionCount(currentMentionCountWithoutNull);
    }
  }, [selectionList.length]);

  useEffect(() => {
    if (managementMode) {
      setSelectionTitle(selectedThematicSelectionOption?.name);
      setSelectionDescription(selectedThematicSelectionOption?.description || '');
      setTotalThematicsMentionCount(currentMentionCountWithoutNull);
    }
  }, [managementMode]);

  const selectAllItems = (obj, keyToFind) => {
    return obj.map(o => {
      if (o.thematicsOverviewDetails) {
        o.thematicsOverviewDetails[keyToFind] = !isGlobalSelected;
        return o;
      }
      Object.entries(o).reduce(a => {
        if (Array.isArray(a[1]) && a[1].length > 0) {
          selectAllItems(a[1], keyToFind);
        }
      });
    });
  };

  const selectAll = () => {
    if (isGlobalSelected) {
      setSelectedThematics([]);
    } else {
      setSelectedThematics(allSelectedCategories);
    }
  };

  const enterCreationMode = () => {
    setSelectedThematics([]);
    setCreationMode(true);
    setSelectionTitle(t('selections:New selection'));
    setSelectionDescription(t('selections:New selection description'));
    dispatchSelectThematicSelection({
      projectId,
      name: t('selections:New selection'),
      thematics: [],
    });
  };

  const deleteSelectionConfirmButton = (
    <Mutation
      mutation={DELETE_THEMATIC_SELECTION_MUTATION}
      onError={error => {
        console.log(error);
      }}
      onCompleted={({ deleteThematicsSelection }) => {
        if (deleteThematicsSelection.errors === null || deleteThematicsSelection.errors.length === 0) {
          toggleDeleteSelectionConfirmationModal(false);
          setManagementMode(false);
          setShowSelection(false);
        }
      }}
    >
      {(deleteSelection, { data, loading, error }) => (
        <StyledQButton
          bgColor="red"
          onClick={() => {
            deleteSelection({
              variables: { projectId, thematicSelectionId: selectedThematicSelectionOption.id },
              refetchQueries: [
                {
                  query: GET_THEMATIC_SELECTION_LIST,
                  variables: {
                    projectId,
                  },
                },
              ],
            });
          }}
          icon="TRASH"
          iconSize={24}
          iconPosition="left"
          disabled={loading}
        >
          <span>{t('button:Delete')}</span>
          {loading && <Loader color={white} />}
          {(error || data?.deleteThematicsSelection?.errors?.length > 0) && <div>ERROR</div>}
        </StyledQButton>
      )}
    </Mutation>
  );

  const currentThematicSelectionOption =
    selectionList.find(selection => selection.id === selectedThematicSelectionOption?.id) || last(selectionList);

  const getListTitle = () => {
    if (creationMode || managementMode) {
      return (
        <EditableTitle
          selectedThematicSelectionOption={selectedThematicSelectionOption}
          selectionTitle={selectionTitle}
          setSelectionTitle={setSelectionTitle}
          selectionDescription={selectionDescription}
          setSelectionDescription={setSelectionDescription}
        />
      );
    }
    if (currentThematicSelectionOption?.name) {
      return (
        <ListTitle
          listTitle={currentThematicSelectionOption?.name || t('selections:Complete list')}
          listDescription={currentThematicSelectionOption?.description || ''}
          loadingThematicOverview={loadingThematicOverview}
        />
      );
    }
    if (selectionList.length < 1) {
      return (
        <ListTitle
          listTitle={t('selections:Complete list')}
          listDescription=""
          loadingThematicOverview={loadingThematicOverview}
        />
      );
    }
  };

  const selectedThematicsVariable = selectedThematics?.map(thematic => {
    return {
      thematic: thematic?.thematic,
      subThematic: thematic?.subthematic,
      subSubThematic: thematic?.subsubthematic,
    };
  });

  const selectedThematicsForSettings = selectedThematics?.map(thematic => {
    return {
      thematic: thematic?.thematic,
      subthematic: thematic?.subThematic || thematic?.subthematic,
      subsubthematic: thematic?.subSubThematic || thematic?.subsubthematic,
    };
  });

  const toggle = () => setTooltipOpen(!tooltipOpen);
  const isAiAvailable = aiUsageExpirationDate && Date.now() < new Date(aiUsageExpirationDate);

  return (
    <StyledQPanel hints={hints} displayCardTitle={false} displayCardSubTitle={false} displayHints>
      {!isAiAvailable && (
        <Tooltip
          isOpen={tooltipOpen}
          target={targetId}
          toggle={toggle}
          autohide={false}
          placement="top"
          style={{ maxWidth: '320px', width: '320px' }}
        >
          <span>{t('Ai features expired', { aiUsageExpirationDate: new Date(aiUsageExpirationDate) })}</span>
          <img src="/apercu_features_ia.png" alt={t('AI features overview')} />
        </Tooltip>
      )}

      <TitleContainer>
        <Title>{t('My categories list')}</Title>
        {message && <EmailMessageContainer>{message.message}</EmailMessageContainer>}
        {getListTitle(loadingThematicOverview)}
        {selectionList.length < 1 && !creationMode && !managementMode && (
          <ButtonsContainer alignRight>
            <QButton
              onClick={() => {
                enterCreationMode();
                setShowSelection(true);
              }}
              bgColor="green"
              hasOpacity
              padding={2}
              id={t('button:Create')}
              tooltipText={t('button:Create')}
              tooltipPlacement="top"
            >
              <Icon icon="PLUS" size={35} color={green} />{' '}
            </QButton>{' '}
          </ButtonsContainer>
        )}
        {selectionList.length > 0 && !creationMode && !managementMode && (
          <ButtonsContainer>
            <ThematicSelectionSelect
              name="thematicSelectionSelect"
              value={currentThematicSelectionOption}
              searchable={false}
              clearable={false}
              placeholder={t('selections:Current selection')}
              onChange={onThematicSelectionChange}
              options={selectionList}
              width={244}
            />
            <QButton
              marginRight={1}
              marginLeft={1}
              fitContent
              onClick={() => {
                setManagementMode(true);
                setShowSelection(true);
              }}
            >
              {t('selections:Manage selection')}{' '}
            </QButton>{' '}
            <QButton
              onClick={() => {
                enterCreationMode();
                setShowSelection(true);
              }}
              bgColor="green"
              hasOpacity
              padding={2}
              id={t('button:Create')}
              tooltipText={t('button:Create')}
              tooltipPlacement="top"
            >
              <Icon icon="PLUS" size={35} color={green} />{' '}
            </QButton>{' '}
          </ButtonsContainer>
        )}
        {creationMode && (
          <ButtonsContainer alignRight>
            <QButton
              onClick={() => {
                setShowSelection(false);
                const cloneSettings = clone(settings);
                cloneSettings.selectedThematics = selectedThematicsForSettings;
                saveThematicsOverviewSettings({
                  variables: {
                    projectId,
                    settings: JSON.stringify(cloneSettings),
                  },
                  refetchQueries: () => {
                    if (!projectId || !range) return [];
                    return [
                      {
                        query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
                        variables: {
                          ...filterKeys,
                          projectId,
                          range,
                          thematicSelectionId: last(selectionList)?.id || null,
                        },
                      },
                    ];
                  },
                  awaitRefetchQueries: true,
                });
                saveThematicSelection({
                  variables: {
                    projectId,
                    name: selectionTitle,
                    thematics: selectedThematicsVariable,
                  },
                  refetchQueries: [
                    {
                      query: GET_THEMATIC_SELECTION_LIST,
                      variables: {
                        projectId,
                      },
                    },
                  ],
                });
                dispatchSelectThematicSelection({
                  projectId,
                  name: selectionTitle,
                  thematics: selectedThematicsVariable,
                });
                setCreationMode(false);
              }}
              bgColor="transparent"
              marginRight={1}
              id={t('button:Save')}
              tooltipText={t('button:Save')}
              tooltipPlacement="top"
            >
              <Icon icon="SAVE" color={black} size={22} />
            </QButton>{' '}
            <QButton
              onClick={() => {
                setCreationMode(false);
                setShowSelection(false);
              }}
            >
              {t('button:Cancel')}
            </QButton>{' '}
          </ButtonsContainer>
        )}
        {managementMode && (
          <>
            <ButtonsContainer alignRight>
              <QButton
                onClick={() => {
                  setShowSelection(false);
                  const cloneSettings = clone(settings);
                  cloneSettings.selectedThematics = selectedThematicsForSettings;
                  saveThematicsOverviewSettings({
                    variables: {
                      projectId,
                      settings: JSON.stringify(cloneSettings),
                      thematicSelectionId: selectedThematicSelectionOption.id,
                    },
                    refetchQueries: () => {
                      if (!projectId || !range) return [];
                      return [
                        {
                          query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
                          variables: {
                            ...filterKeys,
                            projectId,
                            range,
                            thematicSelectionId: selectedThematicSelectionOption.id,
                          },
                        },
                      ];
                    },
                    awaitRefetchQueries: true,
                  });
                  saveThematicSelection({
                    variables: {
                      projectId,
                      id: selectedThematicSelectionOption.id,
                      name: selectionTitle,
                      thematics: selectedThematicsVariable,
                    },
                    refetchQueries: [
                      {
                        query: GET_THEMATIC_SELECTION_LIST,
                        variables: {
                          projectId,
                        },
                      },
                    ],
                  });
                  dispatchSelectThematicSelection({
                    projectId,
                    id: selectedThematicSelectionOption.id,
                    name: selectionTitle,
                    thematics: selectedThematicsVariable,
                  });
                  setManagementMode(false);
                }}
                bgColor="transparent"
                marginRight={1}
                id={t('button:Save')}
                tooltipText={t('button:Save')}
                tooltipPlacement="top"
              >
                <Icon icon="SAVE" color={black} size={22} />
              </QButton>{' '}
              <QButton
                onClick={() => {
                  setManagementMode(false);
                  setShowSelection(false);
                  const cloneSettings = clone(settings);
                  cloneSettings.selectedThematics = selectedThematicsForSettings;
                  saveThematicsOverviewSettings({
                    variables: {
                      projectId,
                      settings: JSON.stringify(cloneSettings),
                      thematicSelectionId: selectedThematicSelectionOption.id,
                    },
                    refetchQueries: () => {
                      if (!projectId || !range) return [];
                      return [
                        {
                          query: GET_THEMATICS_OVERVIEW_DATA_QUERY,
                          variables: {
                            ...filterKeys,
                            projectId,
                            range,
                            thematicSelectionId: last(selectionList)?.id || null,
                          },
                        },
                      ];
                    },
                    awaitRefetchQueries: true,
                  });
                  duplicateThematicSelection({
                    variables: {
                      projectId,
                      name: `${selectedThematicSelectionOption.name} - copy`,
                      // description: selectedThematicSelectionOption.description,
                      thematics: selectedThematicsVariable,
                    },
                  });
                }}
                bgColor="transparent"
                marginRight={1}
                id={t('button:Duplicate')}
                tooltipText={t('button:Duplicate')}
                tooltipPlacement="top"
              >
                <Icon icon="DUPLICATE" color={black} size={22} />
              </QButton>{' '}
              <QButton
                onClick={() => {
                  toggleDeleteSelectionConfirmationModal(!deleteSelectionConfirmationModalOpen);
                }}
                bgColor="transparent"
                marginRight={1}
                id={t('button:Delete')}
                tooltipText={t('button:Delete')}
                tooltipPlacement="top"
              >
                <Icon icon="TRASH" color={red} size={22} />
              </QButton>{' '}
              <QButton
                onClick={() => {
                  setManagementMode(false);
                  setShowSelection(false);
                }}
              >
                {t('button:Cancel')}
              </QButton>{' '}
            </ButtonsContainer>
            <DeleteSelectionConfirmationModal
              isOpen={deleteSelectionConfirmationModalOpen}
              deleteConfirmButton={deleteSelectionConfirmButton}
              onDeleteCanceled={() => toggleDeleteSelectionConfirmationModal(false)}
              selectedThematicSelectionOption={selectedThematicSelectionOption}
            />
          </>
        )}
      </TitleContainer>
      <DisplayAllButton onClick={() => setDisplayAll(!displayAll)}>
        <Icon icon="INTERFACE_FILTER_COLLAPSED" size={24} />{' '}
        {displayAll ? t('button:Collapse view') : t('button:Expand view')}
      </DisplayAllButton>
      <TableContainer>
        <Table borderless>
          <Head>
            <tr>
              {showSelection && (
                <CheckboxCell>
                  <Checkbox selected={isGlobalSelected} onClick={() => selectAll()} />
                </CheckboxCell>
              )}
              <TitleCell>{t('Categories')}</TitleCell>
              <IndicatorCell>{t('Mentions')}</IndicatorCell>
              <IndicatorCell>{t('Verbatim percentage')}</IndicatorCell>
              <IndicatorCell>{t('E-index')}</IndicatorCell>
              <IndicatorCell>
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAADIElEQVR4nO1YTWtTQRSdIvhRFesXWAuCgpuCCIILa2ptmryZV2MbKmntyn0hNu9OgsVNduq+/ooutCtBLLyZebEgBKTqyv6F6i+wRG7bpM/0tXkfk5DIO3Ah0MzknNt775wZQmLEiOELJSpGOVObGMCcBOk1AJUbnKkaBn4mvQRuymSdfD0sQ4yTXgEwtdosgFP5nvQCwBBpoHL7oAD1p0idFOlWWIYYxyx7kt/vhW38TleVU4mKUXfD+g1cg2t9/UZK3ARTZcFQmZe0OqhVwN6orIWMzVb7Lz1yznOmngJV8xhFo/Ikzz6c0CYAmPoZVgCubbW/ZVTG6+RhLwpU3tEowEmELaFWB1yBysFm8oBhyLnSyOpZ0t1NXOuzDMU8BdAdESOkHcBRiSPTa4wCUxN+9ylMfLpxKHmKYc9Dxr7UFhGYZY/sv/O7PpdbOVagcvpoAWo+SEKCl9OB/4Dz0O96ztRwS/J0N6zk2lBXmbml7JeLnDqzfgWAqbLlTLVfvwDmJILYaSwbzHwg8nQ3OBUzPC2vY+OTzqPW9zzlXOPMeRyUODRFiVUmca+OUS9O2leOHJUhg6dlEk/wthF/kaqe45PygW7i0DRmi6a6vzBmn9FKHo2Z29u0OzgVs3iiaxMAhp3tFHloiPA/wn0IUJlOCwCdjwqlVOUqWuLOCRBTi9P2ANGJcu7HcctwboeZ+eA/6znLFLfwfNFK/h8hmWo/Z+Ku1qY25Rzu+WzMPkk6BctYv8DNz0kdY7MU9p6QZ+oyMPWdU/UNP4cSklwb2rn7Bp0ypjAxCSQKgMo3Lu//OlJZUTEThPxKrhatzq3c+ilgasvlPn9HcYhozPyWjRU180ieM/XW48K+jH8LtyteJ+3WHsmIcK3EOsdScWfeQ8QWfidMTxx6oacN8nPlKD5np2EDvECE+Q2vJxXQ9bSC06bdAhanvw54nRFFHY9b7S6hhggq7h2YPEwNE13Ym0DLept4Hzz98XSBCpflEFPa7UHzGOVM/tJ50caGRksMzEloN2Z1YKm4sv+K9BryaCXwvZPKjSg1HyNGjBj/N/4CHLpRmzINzWUAAAAASUVORK5CYII="
                  alt="sparkling--v1"
                />
              </IndicatorCell>
              <IndicatorCell>
                {t('Synthèse')}
                <UpRightSpan>{t('AI')}</UpRightSpan>
              </IndicatorCell>
              <IndicatorCell>
                {t('Enchantement')}
                <UpRightSpan>{t('AI')}</UpRightSpan>
              </IndicatorCell>
              <IndicatorCell>
                {t('Irritant')}
                <UpRightSpan>{t('AI')}</UpRightSpan>
              </IndicatorCell>
              <td>&nbsp;</td>
            </tr>
          </Head>
          <tbody>
            {categories.map((thematic, index) => {
              const thematicMentionCount = thematic.thematicsOverviewDetails.mentionCount;
              const computedVerbatimPercentage = (thematicMentionCount / totalThematicsMentionCount) * 100;
              let verbatimPercentage = '0 %';
              if (!Number.isNaN(computedVerbatimPercentage)) {
                verbatimPercentage = `${computedVerbatimPercentage.toFixed(2)} %`;
              }
              return (
                <TableRow
                  key={thematic}
                  data={thematic}
                  level="thematic"
                  aiUsageExpirationDate={aiUsageExpirationDate}
                  showSelection={showSelection}
                  isGlobalSelected={isGlobalSelected}
                  setGlobalSelected={setGlobalSelected}
                  categories={categories}
                  setCategories={setCategories}
                  thematicIndex={index}
                  setSelectedThematics={setSelectedThematics}
                  selectedThematics={selectedThematics}
                  settings={settings}
                  filterKeys={filterKeys}
                  displayAll={displayAll}
                  selectedThematicSelectionOption={selectedThematicSelectionOption}
                  isAiAvailable={isAiAvailable}
                  setTargetId={setTargetId}
                  setTooltipOpen={setTooltipOpen}
                  t={t}
                  verbatimPercentage={verbatimPercentage}
                />
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
    </StyledQPanel>
  );
};

CategoryHierarchyCard.propTypes = {
  thematics: PropTypes.arrayOf(PropTypes.object).isRequired,
  t: PropTypes.func.isRequired,
  selectionList: PropTypes.arrayOf(PropTypes.object),
  aiUsageExpirationDate: PropTypes.instanceOf(Date),
  selectedThematicSelectionOption: PropTypes.object || null,
  dispatchSelectThematicSelection: PropTypes.func.isRequired,
  dispatchSetThematicSelectionList: PropTypes.func.isRequired,
  currentTab: PropTypes.string.isRequired,
  currentMentionCountWithoutNull: PropTypes.number || null,
};

CategoryHierarchyCard.defaultProps = {
  selectionList: [],
  selectedThematicSelectionOption: null,
  aiUsageExpirationDate: new Date().setDate(new Date().getDate() - 1),
  currentMentionCountWithoutNull: null,
};

const mapDispatchToProps = dispatch => ({
  dispatchSelectThematicSelection: thematicSelection => {
    dispatch(selectThematicSelection(thematicSelection));
  },
  dispatchSetThematicSelectionList: thematicSelectionList => {
    dispatch(setThematicSelectionList(thematicSelectionList));
  },
});

const mapStateToProps = state => ({
  selectionList: get(state, ['thematicSelections', 'list']),
  selectedThematicSelectionOption: get(state, ['thematicSelections', 'selectedThematicSelectionOption']),
  aiUsageExpirationDate: get(state, ['auth', 'user', 'account', 'aiUsageExpirationDate']),
  currentMentionCountWithoutNull: get(state, ['mentions', 'currentMentionCountWithoutNull']),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation('card', ['button', 'selections'])(CategoryHierarchyCard));
